import { Globe, Server, Wrench, ShieldCheck, Receipt, ArrowRight, Check } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal, Stagger, StaggerItem } from "./Reveal";
import { useSectionNav } from "@/lib/scroll";
import { SITE } from "@/lib/site";

const steps = [
  { n: "01", icon: Server, title: "We Build Your Website", text: "Create a professional website around your business." },
  { n: "02", icon: Globe, title: "Connect Your Custom Domain", text: "Connect your chosen custom domain to your website." },
  { n: "03", icon: Wrench, title: "We Launch It & Take Care of It", text: "We launch your new website and continue maintaining and supporting it through the ongoing monthly service." },
];

const included = [
  "Keeping the website online", "Website maintenance and updates", "General website support",
  "Maintaining the website's functionality", "Monitoring the website",
  "Minor content or website adjustments where applicable", "Keeping the website connected to its custom domain",
];

export const WebsiteCare = () => {
  const goTo = useSectionNav();
  return (
    <section id="website-care" className="section overflow-hidden" aria-labelledby="care-title">
      <div className="orb right-0 top-1/3 h-[420px] w-[420px] bg-violet-600/15" aria-hidden="true" />
      <div className="container-x relative">
        <SectionHeading
          id="care-title"
          eyebrow="Website Care · Domain · Maintenance"
          title="Your Website, Kept Running."
          subtitle={`We launch your new website and take care of it as well. Once your website is built and live, ${SITE.name} continues looking after it for you with ongoing website maintenance and support. We don't just build your website — we launch it and keep it running.`}
        />

        <div className="mt-16 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-6">
            <Stagger className="grid gap-4 sm:grid-cols-3">
              {steps.map(({ n, icon: Icon, title, text }) => (
                <StaggerItem key={n}>
                  <div className="card-dark card-hover h-full p-6" data-testid={`care-step-${n}`}>
                    <div className="flex items-center justify-between">
                      <span className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-indigo-500/25 to-violet-500/10 border border-white/10 text-indigo-200"><Icon className="h-5 w-5" aria-hidden="true" /></span>
                      <span className="font-mono text-xs text-indigo-300/70">{n}</span>
                    </div>
                    <h3 className="mt-6 text-lg font-semibold text-white leading-snug">{title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{text}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>

            <Reveal>
              <div className="card-dark p-7 sm:p-8">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-5 w-5 text-indigo-300" aria-hidden="true" />
                  <h3 className="text-lg font-semibold text-white">Ongoing website maintenance can include</h3>
                </div>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {included.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-emerald-500/15 text-emerald-300"><Check className="h-3 w-3" aria-hidden="true" /></span>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <div className="space-y-6">
            <Reveal delay={0.1}>
              <div className="relative rounded-3xl p-[1px] bg-gradient-to-b from-indigo-400/50 via-violet-500/25 to-transparent">
                <div className="rounded-3xl bg-surface p-7 sm:p-8 h-full" data-testid="care-pricing-card">
                  <div className="flex items-center justify-between gap-3">
                    <p className="eyebrow">Monthly pricing</p>
                    <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">AUD · GST inc.</span>
                  </div>
                  <div className="mt-6 grid gap-3">
                    <div className="flex items-center justify-between gap-4 rounded-2xl border border-indigo-400/25 bg-indigo-500/[0.08] p-5" data-testid="care-price-care">
                      <div className="flex items-center gap-4">
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-indigo-500/20 border border-white/10 text-indigo-200"><Wrench className="h-5 w-5" aria-hidden="true" /></span>
                        <div>
                          <h4 className="font-semibold text-white">Website Care</h4>
                          <p className="mt-0.5 text-xs text-slate-400">Hosting, maintenance & support</p>
                        </div>
                      </div>
                      <p className="text-right"><span className="font-display text-3xl font-semibold text-white">${SITE.pricing.care}</span><span className="block text-xs text-slate-500">per month</span></p>
                    </div>
                    <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5" data-testid="care-price-domain">
                      <div className="flex items-center gap-4">
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/[0.05] border border-white/10 text-violet-200"><Globe className="h-5 w-5" aria-hidden="true" /></span>
                        <div>
                          <h4 className="font-semibold text-white">Custom Domain Service</h4>
                          <p className="mt-0.5 text-xs text-slate-400">Your own domain, connected & kept active</p>
                        </div>
                      </div>
                      <p className="text-right"><span className="font-display text-3xl font-semibold text-white">${SITE.pricing.domain}</span><span className="block text-xs text-slate-500">per month</span></p>
                    </div>
                    <div className="flex items-center justify-between gap-4 rounded-2xl border border-dashed border-white/15 px-5 py-4" data-testid="care-price-total">
                      <p className="text-sm text-slate-300">Website Care + Custom Domain together</p>
                      <p className="text-right"><span className="font-display text-2xl font-semibold text-white">${SITE.pricing.care + SITE.pricing.domain}</span><span className="block text-xs text-slate-500">per month</span></p>
                    </div>
                  </div>
                  <div className="mt-6 flex gap-3 border-t border-white/[0.08] pt-5">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/[0.05] border border-white/10 text-violet-200"><Receipt className="h-4 w-4" aria-hidden="true" /></span>
                    <p className="text-sm text-slate-400 leading-relaxed">Prices are in Australian dollars and include GST. An invoice will be sent to you when your next payment is approaching, with the amount due and payment details clearly shown. Your website development cost is quoted separately.</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="card-dark p-7 sm:p-8" data-testid="care-billing-card">
                <p className="eyebrow">Billing, clearly</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5">
                    <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">One-time</p>
                    <h4 className="mt-2 font-semibold text-white">Website development</h4>
                    <p className="mt-2 text-sm text-slate-400">Quoted based on your requirements. A ${SITE.deposit} deposit is required to begin, and most websites are completed in {SITE.timeline} depending on the project.</p>
                  </div>
                  <div className="rounded-2xl border border-indigo-400/20 bg-indigo-500/[0.06] p-5">
                    <p className="font-mono text-[11px] uppercase tracking-widest text-indigo-300/80">Ongoing · Monthly</p>
                    <h4 className="mt-2 font-semibold text-white">Website service</h4>
                    <p className="mt-2 text-sm text-slate-400">Website Care ${SITE.pricing.care}/month for hosting, maintenance and support, plus Custom Domain Service ${SITE.pricing.domain}/month where applicable (AUD, GST inclusive).</p>
                  </div>
                </div>
                <ul className="mt-5 space-y-2 text-sm text-slate-400">
                  <li>· The website development cost is separate from ongoing monthly website services.</li>
                  <li>· The ongoing monthly service (${SITE.pricing.care}/month) and custom domain service (${SITE.pricing.domain}/month) are paid by the customer.</li>
                  <li>· Your monthly payment keeps your website and custom domain running and allows {SITE.name} to continue providing maintenance and support.</li>
                  <li>· You'll receive an invoice before payment is due.</li>
                  <li>· The monthly service continues while you choose to keep the website service active.</li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal className="mt-12 flex flex-col sm:flex-row gap-3.5">
          <button onClick={() => goTo("quote")} className="btn-primary group" data-testid="care-section-primary-cta">
            Keep My Website Running <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </button>
          <button onClick={() => goTo("quote")} className="btn-secondary" data-testid="care-section-quote-cta">Get a Website Quote</button>
        </Reveal>
      </div>
    </section>
  );
};
